import * as Cookies from "js-cookie";

/**
 * Cognito JWTs often exceed per-cookie size limits when Access + Id + Refresh are
 * all stored as cookies. Downloads / image APIs need the IdToken header; if the
 * cookie was dropped, fall back to sessionStorage / localStorage.
 */
export const OPERATOR_ID_TOKEN_KEY = "SessionIdToken";
export const OPERATOR_ACCESS_TOKEN_KEY = "SessionAccessToken";
export const OPERATOR_REFRESH_TOKEN_KEY = "SessionRefreshToken";
export const OPERATOR_EMAIL_KEY = "SessionEmailAddress";

function readStorage(key: string): string | undefined {
  try {
    const fromSession = sessionStorage.getItem(key);
    if (fromSession) return fromSession;
  } catch {
    /* private mode / blocked */
  }
  try {
    const fromLocal = localStorage.getItem(key);
    if (fromLocal) return fromLocal;
  } catch {
    /* private mode / blocked */
  }
  return undefined;
}

function writeStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function clearStorage(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Prefer cookie, then session/local storage (IdToken often too large for cookies). */
export function getOperatorIdToken(): string | undefined {
  const fromCookie = Cookies.get(OPERATOR_ID_TOKEN_KEY);
  if (fromCookie && fromCookie.trim()) return fromCookie.trim();
  const fromStore = readStorage(OPERATOR_ID_TOKEN_KEY);
  return fromStore?.trim() || undefined;
}

export function getOperatorAccessToken(): string | undefined {
  const fromCookie = Cookies.get(OPERATOR_ACCESS_TOKEN_KEY);
  if (fromCookie && fromCookie.trim()) return fromCookie.trim();
  return readStorage(OPERATOR_ACCESS_TOKEN_KEY)?.trim() || undefined;
}

export function getOperatorRefreshToken(): string | undefined {
  const fromCookie = Cookies.get(OPERATOR_REFRESH_TOKEN_KEY);
  if (fromCookie && fromCookie.trim()) return fromCookie.trim();
  return readStorage(OPERATOR_REFRESH_TOKEN_KEY)?.trim() || undefined;
}

export function getOperatorEmailCookie(): string | undefined {
  const fromCookie = Cookies.get(OPERATOR_EMAIL_KEY);
  if (fromCookie && fromCookie.trim()) return fromCookie.trim();
  return readStorage(OPERATOR_EMAIL_KEY)?.trim() || undefined;
}

export function persistOperatorSessionTokens(data: {
  AccessToken?: string | null;
  IdToken?: string | null;
  RefreshToken?: string | null;
  EmailAddress?: string | null;
}): void {
  const pairs: [string, string | null | undefined][] = [
    [OPERATOR_ACCESS_TOKEN_KEY, data.AccessToken],
    [OPERATOR_ID_TOKEN_KEY, data.IdToken],
    [OPERATOR_REFRESH_TOKEN_KEY, data.RefreshToken],
    [OPERATOR_EMAIL_KEY, data.EmailAddress],
  ];
  pairs.forEach(([key, value]) => {
    if (value) {
      Cookies.set(key, value, { expires: 14 });
      writeStorage(key, value);
    } else {
      Cookies.remove(key);
      clearStorage(key);
    }
  });
}

export function clearOperatorSessionTokens(): void {
  [
    OPERATOR_ACCESS_TOKEN_KEY,
    OPERATOR_ID_TOKEN_KEY,
    OPERATOR_REFRESH_TOKEN_KEY,
    OPERATOR_EMAIL_KEY,
  ].forEach((key) => {
    Cookies.remove(key);
    clearStorage(key);
  });
}
